import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { scrollToSection } from '../utils/scrollToSection';

const About = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (path, sectionId) => {
    if (window.location.pathname === path) {
      scrollToSection(sectionId);
    } else {
      navigate(path);
      setTimeout(() => {
        if (path === '/donate') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          scrollToSection(sectionId);
        }
      }, 500);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            About Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Committed to bringing healthcare to those who need it most
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <Card className="border-2 border-blue-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
                  <Heart className="h-8 w-8 text-white fill-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Our Mission
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  {mockData.about.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-100 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Our Vision
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  {mockData.about.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission Work Showcase */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/Batroun Mission/batroun_1.jpg"
                alt="Batroun mission"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/Afqa Mission/afqa_2.jpg"
                alt="Afqa mission"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="overflow-hidden rounded-lg shadow-lg">
              <img 
                src="/images/Tanzania Mission/tanzania_4.jpg"
                alt="Tanzania mission"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Our Story
          </h2>
          <div className="prose prose-lg max-w-none">
            {mockData.about.story.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-lg text-gray-600 leading-relaxed mb-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Meet Our Founder
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden shadow-xl border-none">
              <div className="md:flex">
                <div className="md:w-1/3 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center overflow-hidden">
                  <img 
                    src="/images/DrFayezElHachem2.jpeg"
                    alt="Dr. Fayez El Hachem"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="md:w-2/3 p-8 md:p-12">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {mockData.about.founder.name}
                  </h3>
                  <p className="text-lg text-blue-600 font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {mockData.about.founder.role}
                  </p>
                  <p className="text-gray-500 mb-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {mockData.about.founder.location}
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {mockData.about.founder.bio}
                  </p>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Impact */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Our Impact in 2024
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Through 5 missions across Lebanon and Tanzania, we've brought healthcare to hundreds of people in need.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>650+</div>
              <div className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>Patients Treated</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>5</div>
              <div className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>Missions Completed</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-blue-600 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>2</div>
              <div className="text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>Countries Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Missions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Upcoming Missions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockData.projects.filter(p => p.status === 'upcoming').map((project) => (
              <Card 
                key={project.id} 
                className="overflow-hidden border-white/20 hover:shadow-xl transition-all cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                {/* Blurred Background Image */}
                <div className="relative">
                  <div className="h-48 relative overflow-hidden">
                    <img 
                      src={project.thumbnail}
                      alt={project.title}
                      className="w-full h-full object-cover blur-sm opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-600/50 to-blue-600/80"></div>
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-orange-500 text-white">Coming Soon</Badge>
                    </div>
                  </div>
                  <CardContent className="p-8 bg-white">
                    <div className="text-sm font-semibold uppercase tracking-wider mb-2 text-blue-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {project.date}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {project.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      {project.description}
                    </p>
                    <div className="text-sm font-semibold text-green-600">
                      {project.impact}
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Be Part of Our Mission
          </h2>
          <p className="text-xl text-gray-600 mb-12" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Join us in bringing healthcare to communities that need it most. Your support saves lives.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => handleScrollToSection('/donate', 'donation-form')}
              className="bg-red-500 hover:bg-red-600 text-white text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
            >
              Donate Now
            </Button>
            <Button 
              onClick={() => handleScrollToSection('/get-involved', 'volunteer-form')}
              variant="outline" 
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
            >
              Volunteer With Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;